import { useQueryClient } from "@tanstack/react-query";
import { Info, LogIn, MailCheck, MailX, RotateCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { qk, useSession } from "../api/queries";
import { Brand } from "../app/Brand";
import { LocaleMenu } from "../app/LocaleMenu";
import { useT } from "../i18n/core";
import { LoadingRows } from "../ui/Common";
import { confirmEmailMessages } from "./confirmEmail.i18n";
import { loginMessages } from "./login.i18n";

const TOKEN_PREFIX = "ebe1_";

/** O segredo chega no fragmento (/confirm-email#ebe1_…), que não vai ao servidor (ADR 0020). */
function readToken(): string {
  const hash = window.location.hash;
  const token = hash.slice(hash.lastIndexOf("#") + 1);
  return token.startsWith(TOKEN_PREFIX) ? token : "";
}

/** Tira o segredo da barra de endereço e do histórico; no roteamento por hash, mantém a rota. */
function removeTokenFromUrl() {
  const { hash, pathname, search } = window.location;
  const i = hash.lastIndexOf("#" + TOKEN_PREFIX);
  if (i >= 0) window.history.replaceState(window.history.state, "", pathname + search + hash.slice(0, i));
}

export function ConfirmEmailPage() {
  const t = useT(confirmEmailMessages);
  const tl = useT(loginMessages);
  const [token] = useState(readToken);

  useEffect(() => {
    removeTokenFromUrl();
  }, []);

  return (
    <div className="login">
      <aside className="login-side">
        <Brand />
        <div className="login-quote">
          <h2>{tl.headline}</h2>
          <p>{tl.pitch}</p>
        </div>
        <span />
      </aside>
      <div className="login-form">
        <div className="login-lang"><LocaleMenu variant="plain" /></div>
        <div className="login-card">
          {token ? (
            <Confirm token={token} />
          ) : (
            <>
              <h1>{t.missingTitle}</h1>
              <div className="callout crit" role="alert"><Info aria-hidden="true" /><span>{t.missing}</span></div>
              <Exit />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

type State = { kind: "working" } | { kind: "done"; email: string } | { kind: "error"; error: unknown };

function Confirm({ token }: { token: string }) {
  const t = useT(confirmEmailMessages);
  const api = useApi();
  const qc = useQueryClient();
  const [state, setState] = useState<State>({ kind: "working" });
  const started = useRef(false);

  async function confirm() {
    try {
      const r = await api.confirmEmailChange(token);
      setState({ kind: "done", email: r.email });
      // Sessão aberta neste navegador (se houver) e listas de pessoas passam a mostrar o e-mail novo.
      void qc.invalidateQueries({ queryKey: qk.session });
      void qc.invalidateQueries({ queryKey: ["org"] });
    } catch (err) {
      setState({ kind: "error", error: err });
    }
  }

  useEffect(() => {
    // StrictMode executa o efeito duas vezes; o link só vale uma vez.
    if (started.current) return;
    started.current = true;
    void confirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- confirmação única ao montar
  }, []);

  if (state.kind === "working") {
    return (
      <>
        <h1>{t.workingTitle}</h1>
        <p className="ink-2" role="status">{t.working}</p>
        <LoadingRows rows={2} />
      </>
    );
  }

  if (state.kind === "done") {
    return (
      <>
        <h1>{t.doneTitle}</h1>
        <div className="callout ok" role="status">
          <MailCheck aria-hidden="true" />
          <span style={{ overflowWrap: "anywhere" }}>{t.done(state.email)}</span>
        </div>
        <Exit primary />
      </>
    );
  }

  const err = state.error;
  const status = err instanceof ApiRequestError ? err.status : 0;
  // Rede, limite de tentativas ou falha do servidor: o link ainda pode valer.
  const transient = status === 0 || status === 429 || status >= 500;
  const title = status === 409 ? t.takenTitle : transient ? t.transientTitle : t.invalidTitle;
  const hint = status === 409 ? t.takenHint : transient ? null : t.invalidHint;

  function retry() {
    setState({ kind: "working" });
    void confirm();
  }

  return (
    <>
      <h1>{title}</h1>
      <div className="callout crit" role="alert">
        <MailX aria-hidden="true" />
        <span>{err instanceof ApiRequestError ? err.message : t.failed}</span>
      </div>
      {hint && <p className="ink-2">{hint}</p>}
      {transient && (
        <button className="btn btn-primary" type="button" style={{ height: 40 }} onClick={retry}>
          <RotateCw aria-hidden="true" /> {t.retry}
        </button>
      )}
      <Exit primary={!transient} />
    </>
  );
}

/** Volta para a console com sessão aberta, ou para a entrada sem ela. */
function Exit({ primary = false }: { primary?: boolean }) {
  const t = useT(confirmEmailMessages);
  const navigate = useNavigate();
  const session = useSession();
  const signedIn = Boolean(session.data);
  return (
    <button
      className={primary ? "btn btn-primary" : "btn"} type="button" style={primary ? { height: 40 } : undefined}
      onClick={() => navigate(signedIn ? "/" : "/login", { replace: true })}
    >
      <LogIn aria-hidden="true" /> {signedIn ? t.goToConsole : t.goToLogin}
    </button>
  );
}
